import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Sun, Moon, Menu, X } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const { theme, toggleTheme } = useTheme();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const isHeroHeader = !isScrolled;
  const headerClass = isHeroHeader
    ? 'bg-transparent'
    : theme === 'light'
      ? 'bg-white/92 backdrop-blur-xl shadow-lg border-b border-slate-200/80'
      : 'bg-slate-950/78 backdrop-blur-xl shadow-lg border-b border-white/10';
  const navLinkClass = isHeroHeader
    ? 'text-white hover:text-cyan-300'
    : theme === 'light'
      ? 'text-slate-900 hover:text-blue-600'
      : 'text-white hover:text-cyan-300';
  const toggleButtonClass = isHeroHeader
    ? 'border border-white/15 bg-white/10 hover:bg-white/20 backdrop-blur-md'
    : theme === 'light'
      ? 'bg-slate-100 hover:bg-slate-200'
      : 'bg-gray-800 hover:bg-gray-700';
  const toggleIconClass = isHeroHeader
    ? 'text-white'
    : theme === 'light'
      ? 'text-slate-900'
      : 'text-yellow-400';
  const menuButtonClass = isHeroHeader
    ? 'border border-white/15 bg-white/10 hover:bg-white/20 backdrop-blur-md'
    : theme === 'light'
      ? 'bg-slate-100 hover:bg-slate-200'
      : 'bg-gray-800 hover:bg-gray-700';
  const menuIconClass = isHeroHeader
    ? 'text-white'
    : theme === 'light'
      ? 'text-slate-900'
      : 'text-gray-300';
  const mobileMenuClass = isHeroHeader
    ? 'border-white/10 bg-slate-950/92'
    : theme === 'light'
      ? 'border-slate-200/80 bg-white/95'
      : 'border-white/10 bg-slate-950/92';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerClass}`}
    >
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => scrollToSection('hero')}>
            <img
              src="/logo.png"
              alt="Kaamhub Logo"
              className="h-12 w-12 object-contain"
            />
            <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
              Kaamhub
            </span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('about')}
              className={`${navLinkClass} transition-colors font-medium`}
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('features')}
              className={`${navLinkClass} transition-colors font-medium`}
            >
              Features
            </button>
            <button
              onClick={() => scrollToSection('how-it-works')}
              className={`${navLinkClass} transition-colors font-medium`}
            >
              How It Works
            </button>
            <button
              onClick={() => scrollToSection('testimonials')}
              className={`${navLinkClass} transition-colors font-medium`}
            >
              Testimonials
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className={`${navLinkClass} transition-colors font-medium`}
            >
              Contact
            </button>
          </nav>

          {/* Actions */}
          <div className="flex items-center space-x-4">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-all duration-300 transform hover:scale-110 ${toggleButtonClass}`}
              aria-label="Toggle theme"
            >
              {theme === 'light' ? (
                <Moon className={`w-5 h-5 ${toggleIconClass}`} />
              ) : (
                <Sun className={`w-5 h-5 ${toggleIconClass}`} />
              )}
            </button>

            {/* Download Button */}
            <Button
              onClick={() => scrollToSection('download')}
              className="hidden md:flex bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-6 py-2 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Download App
            </Button>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden p-2 rounded-lg transition-colors ${menuButtonClass}`}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className={`w-6 h-6 ${menuIconClass}`} />
              ) : (
                <Menu className={`w-6 h-6 ${menuIconClass}`} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className={`md:hidden py-4 border-t backdrop-blur-xl rounded-b-2xl ${mobileMenuClass}`}>
            <nav className="flex flex-col space-y-4">
              <button
                onClick={() => scrollToSection('about')}
                className={`${navLinkClass} text-left transition-colors font-medium py-2`}
              >
                About
              </button>
              <button
                onClick={() => scrollToSection('features')}
                className={`${navLinkClass} text-left transition-colors font-medium py-2`}
              >
                Features
              </button>
              <button
                onClick={() => scrollToSection('how-it-works')}
                className={`${navLinkClass} text-left transition-colors font-medium py-2`}
              >
                How It Works
              </button>
              <button
                onClick={() => scrollToSection('testimonials')}
                className={`${navLinkClass} text-left transition-colors font-medium py-2`}
              >
                Testimonials
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className={`${navLinkClass} text-left transition-colors font-medium py-2`}
              >
                Contact
              </button>
              <Button
                onClick={() => scrollToSection('download')}
                className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-6 py-2 rounded-lg font-semibold transition-all duration-300"
              >
                Download App
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
